import axios from 'axios';
import querystring from 'qs';
import { authService } from '.';
import { captureException } from '@sentry/nextjs';
import { httpKeepAliveAgent, httpsKeepAliveAgent } from '../helpers/utils/connectionPoolConfig';

const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL;
const TIMEOUT = process.env.NEXT_PUBLIC_TIMEOUT ? parseInt(process.env.NEXT_PUBLIC_TIMEOUT) : 40000;

const isServer = typeof window === 'undefined';
const api = axios.create({
  httpAgent: httpKeepAliveAgent,
  httpsAgent: httpsKeepAliveAgent,
  baseURL: BASE_URL,
  timeout: TIMEOUT,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  paramsSerializer: (params) => querystring.stringify(params),
});

const APIResponseValidation = async (response, promise?, showErrorPage = false, auth = true) => {
  // prevent refresh token checking when func executed in server or auth parameter is false
  if (isServer) return promise;

  // show toast error message if API not success
  if (response?.status && response?.status !== 200) {
    // check refresh token
    if (showErrorPage) {
      //   APIResponseErrorValidation({ response });
      return promise;
    }
  }
  return promise;
};

const APIInstance = {
  /**
   * @param {Sring} url '/path/to/endpoint'
   * @param {Object} json
   * @param {Object} form
   */
  put: (
    url,
    form = {},
    json = {},
    customConfig = {},
    auth = true,
    toastError = false,
    showErrorPage = false
  ) => {
    api.defaults.headers.common['Content-Type'] = json
      ? 'application/json'
      : 'application/x-www-form-urlencoded';
    const data = querystring.stringify(form) || json;
    return api
      .put(url, data, {
        params: querystring.stringify(form),
        ...customConfig,
      })
      .then((response) => {
        return APIResponseValidation(
          response,
          Promise.resolve(response),
          // toastError,
          showErrorPage,
          auth
        );
      })
      .catch((err) => {
        console.log(toastError ? '' : '');
        return APIResponseValidation(
          err.response,
          Promise.reject(err),
          // toastError,
          showErrorPage,
          auth
        );
      });
  },

  /**
   * @param {Sring} url '/path/to/endpoint'
   * @param {Object} param query params
   */
  get: (url, customConfig = {}, auth = true, toastError = true, showErrorPage = true) => {
    return api
      .get(url, {
        ...customConfig,
      })
      .then((response) => {
        return APIResponseValidation(
          response,
          Promise.resolve(response),
          // toastError,
          showErrorPage,
          auth
        );
      })
      .catch((err) => {
        console.log(toastError ? '' : '');
        return APIResponseValidation(
          err.response,
          Promise.reject(err),
          // toastError,
          showErrorPage,
          auth
        );
      });
  },

  /**
   * @param {Sring} url '/path/to/endpoint'
   * @param {Object} json
   * @param {Object} form
   * @param {Object} reqConfig  custom config for request
   */
  post: (url, form = null, json = {}, reqConfig = {}, auth = true, showErrorPage = false) => {
    api.defaults.headers['Content-Type'] = form
      ? 'application/x-www-form-urlencoded'
      : 'application/json';
    const data = querystring.stringify(form) || json;
    return api
      .post(url, data, {
        params: querystring.stringify(form),
        ...reqConfig,
      })
      .then((response) => {
        return APIResponseValidation(
          response,
          Promise.resolve(response),
          // toastError,
          showErrorPage,
          auth
        );
      })
      .catch((err) => {
        return APIResponseValidation(
          err.response,
          Promise.reject(err),
          // toastError,
          showErrorPage,
          auth
        );
      });
  },

  /**
   * Send request with Content-Type multipart/form
   * used to upload file
   * @param {Sring} url '/path/to/endpoint'
   * @param {Object} data
   */
  postData: (base_url, url, data = {}, customConfig = {}, auth = true, showErrorPage = false) => {
    api.defaults.headers['Content-Type'] = 'multipart/form-data';
    api.defaults.timeout = TIMEOUT;
    const formData = new FormData();
    const keys = Object.keys(data);
    keys.map((key) => {
      if (data[key] instanceof File) {
        formData.append(key, data[key], data[key].name);
      } else {
        formData.append(key, data[key]);
      }
    });
    return api
      .post(url, formData, { baseURL: base_url || BASE_URL, ...customConfig })
      .then((response) => {
        return APIResponseValidation(
          response,
          Promise.resolve(response),
          // toastError,
          showErrorPage,
          auth
        );
      })
      .catch((err) => {
        return APIResponseValidation(
          err.response,
          Promise.reject(err),
          // toastError,
          auth
        );
      });
  },

  /**
   * @param {Sring} url '/path/to/endpoint'
   * @param {Object} params
   * {
   *   id: [1,2,3]
   * }
   */
  delete: (url, params, auth = true, toastError = true, showErrorPage = false) => {
    let newUrl = url;
    if (params) {
      const qparam = querystring.stringify(params);
      newUrl = `${newUrl}?${qparam}`;
    }
    return api
      .delete(newUrl)
      .then((response) => {
        return APIResponseValidation(
          response,
          Promise.resolve(response),
          // toastError,
          showErrorPage,
          auth
        );
      })
      .catch((err) => {
        console.log(toastError ? '' : '');
        return APIResponseValidation(
          err.response,
          Promise.reject(err),
          // toastError,
          showErrorPage,
          auth
        );
      });
  },
  /**
   * @param {Sring} url '/path/to/endpoint'
   * @param {Object} payload
   * {
   *   id: [1,2,3]
   * }
   */
  deleteData: (url, payload, auth = true, toastError = true, showErrorPage) => {
    return (
      api
        .delete(url, { data: payload })
        // .delete(url, { data: payload }, ((auth = true), (toastError = true)))
        .then((response) => {
          return APIResponseValidation(
            response,
            Promise.resolve(response),
            // toastError,
            showErrorPage,
            auth
          );
        })
        .catch((err) => {
          console.log(toastError ? '' : '');
          return APIResponseValidation(
            err.response,
            Promise.reject(err),
            // toastError,
            showErrorPage,
            auth
          );
        })
    );
  },
  /**
   * Send request with Content-Type multipart/form
   * used to upload file
   * @param {Sring} url '/path/to/endpoint'
   * @param {Object} data
   */
  putData: (url, data = {}, customConfig = {}, auth = true, toastError = true, showErrorPage) => {
    api.defaults.headers['Content-Type'] = 'multipart/form-data';
    api.defaults.timeout = TIMEOUT;
    const formData = new FormData();
    const keys = Object.keys(data);
    keys.map((key) => {
      if (data[key] instanceof File) {
        formData.append(key, data[key], data[key].name);
      } else {
        formData.append(key, data[key]);
      }
    });
    return api
      .put(url, formData, { ...customConfig })
      .then((response) => {
        return APIResponseValidation(
          response,
          Promise.resolve(response),
          // toastError,
          showErrorPage,
          auth
        );
      })
      .catch((err) => {
        console.log(toastError ? '' : '');
        return APIResponseValidation(
          err.response,
          Promise.reject(err),
          // toastError,
          showErrorPage,
          auth
        );
      });
  },

  /**
   * @param {Sring} url '/path/to/endpoint'
   * @param {Object} json
   * @param {Object} form
   * @param {Object} reqConfig  custom config for request
   */
  patch: (url, form = null, json = {}, reqConfig = {}, auth = true, showErrorPage = false) => {
    api.defaults.headers['Content-Type'] = form
      ? 'application/x-www-form-urlencoded'
      : 'application/json';

    const data = querystring.stringify(form) || json;
    return api
      .patch(url, data, {
        params: querystring.stringify(form),
        ...reqConfig,
      })
      .then((response) => {
        return APIResponseValidation(response, Promise.resolve(response), showErrorPage, auth);
      })
      .catch((err) => {
        return APIResponseValidation(err.response, Promise.reject(err), showErrorPage, auth);
      });
  },
  getPoolStats: () => {
    return {
      http: api?.defaults?.httpAgent?.getCurrentStatus(),
      https: api?.defaults?.httpsAgent?.getCurrentStatus(),
    };
  },
};

api.interceptors.request.use(
  async (config) => {
    if (!config.headers?.Authorization && config.url?.includes('saphybris-proxy/v1/saphybris')) {
      const res = await authService.getSystemAuth();
      config.headers.Authorization = `Bearer ${res.data?.token}`;
    }
    return config;
  },
  (error) => {
    // Capture error to sentry
    captureException(error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  async (response) => response,
  async (error) => {
    // capture error to sentry
    captureException(error);
    const originalRequest = error.config;
    if (
      originalRequest?.url?.includes('saphybris-proxy/v1/saphybris') ||
      originalRequest?.method === 'post'
    ) {
      if (
        (error?.response?.status === 401 || error?.response?.status === 403) &&
        !originalRequest._retry
      ) {
        try {
          originalRequest._retry = true;
          //hit next js api to renew system_token cookie
          const res = await authService.getSystemAuth(true);
          originalRequest.headers['Authorization'] = `Bearer ${res.data?.token}`;
          return api(originalRequest);
        } catch (_) {
          return Promise.reject(error);
        }
      }
    }
    return Promise.reject(error);
  }
);

if (process.env.NEXT_PUBLIC_CONNECTION_POOL_STATUS === 'true' && typeof window === 'undefined') {
  setInterval(() => {
    const stats = APIInstance.getPoolStats();
    console.log('Connection Pool Stats:', stats);
  }, 30000); // Log every 30 seconds
}

export default APIInstance;
